import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Modal, Button, Form } from "react-bootstrap";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import CryptoJS from "crypto-js";
import Dropdown from "react-dropdown";
import "react-dropdown/style.css";
import { paymentActions } from "../store/payment.slice";
import HttpStatus from "../helpers/status.enum";
import SpinnerLoader from "./SpinnerLoader";
import EarlyCheckoutForm from "./EarlyCheckoutForm";
import { toast } from "react-toastify";

const encryKey = process.env.REACT_APP_STRIPE_ENCRYPT_KEY;

/**
 * Shared early check-in purchase modal.
 * Used by both checkingin.js (needToEmailName=false) and reservationinfo.js.
 * Manages all internal payment state; parent only supplies data and callbacks.
 */
const EarlyCheckinPurchaseModal = ({
  show,
  onClose,
  onPurchased,
  bookingSlug,
  cardTitle,
  cardBody,
  timeInterval = [],
  initialInterval = "",
  freeEarlyCheckinAlert = "",
  needToEmailName = false,
  isManualPresell = false,
  presellPricingMode = null,
}) => {
  const dispatch = useDispatch();

  const [selectedInterval, setSelectedInterval] = useState(initialInterval);
  const [selectedIntervalLabel, setSelectedIntervalLabel] = useState(
    timeInterval[0]?.label || ""
  );
  const [isIntentCreate, setIsIntentCreate] = useState(false);
  const [isIntentModelShow, setIsIntentModelShow] = useState(false);
  const [isFreeCheckout, setIsFreeCheckout] = useState(false);
  const [clientSecret, setClientSecret] = useState(false);
  const [paymentSlug, setPaymentSlug] = useState("");
  const [isDesabled, setIsDesabled] = useState(needToEmailName);
  const [loader, setLoader] = useState(false);
  const [checkoutName, setCheckoutName] = useState("");
  const [checkoutEmail, setCheckoutEmail] = useState("");
  const [stripeInstance, setStripeInstance] = useState(
    () => loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  );

  // Sync selectedInterval when new ad data arrives
  useEffect(() => {
    if (timeInterval.length > 0) {
      setSelectedInterval(timeInterval[0]?.value || "");
      setSelectedIntervalLabel(timeInterval[0]?.label || "");
    }
  }, [timeInterval]);

  // Reset internal state when modal closes
  useEffect(() => {
    if (!show) {
      setIsIntentCreate(false);
      setIsIntentModelShow(false);
      setIsFreeCheckout(false);
      setClientSecret(false);
      setPaymentSlug("");
      setIsDesabled(needToEmailName);
      setLoader(false);
      setCheckoutName("");
      setCheckoutEmail("");
    }
  }, [show]);

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSelectChange = (e) => {
    setSelectedInterval(e?.value);
    setSelectedIntervalLabel(e?.label);
    const isFree = e?.value === "free";
    setIsFreeCheckout(isFree);
    if (needToEmailName) {
      const isEmailValid = validateEmail(checkoutEmail);
      setIsDesabled(!(checkoutName.length > 2 && isEmailValid));
    } else {
      setIsDesabled(false);
    }
  };

  const handleNameChange = (event) => {
    const value = event.target.value;
    setCheckoutName(value);
    setIsDesabled(!(value.length > 2 && validateEmail(checkoutEmail)));
  };

  const handleEmailChange = (event) => {
    const value = event.target.value;
    setCheckoutEmail(value);
    setIsDesabled(!(checkoutName.length > 2 && validateEmail(value)));
  };

  const createPaymentIntent = async () => {
    if (!selectedInterval) {
      toast("Please select an early check-in option.");
      return;
    }
    setLoader(true);
    setClientSecret(false);
    setIsIntentCreate(false);
    setIsIntentModelShow(false);
    const formData = {
      booking_slug: bookingSlug,
      intent_type: 3,
      needToEmailName,
      checkout_name: checkoutName,
      checkout_email: checkoutEmail,
      time_interval: selectedInterval,
      is_presell_request: isManualPresell ? true : false,
      presell_pricing_mode: presellPricingMode,
    };
    const result = await dispatch(paymentActions.createPaymentIntent(formData));
    const responses = result?.payload;
    if (responses?.status === HttpStatus.HTTP_OK) {
      const paymentRes = responses.result;
      setClientSecret(paymentRes?.client_secret);
      setPaymentSlug(paymentRes?.payment_slug);
      setLoader(false);
      setIsIntentCreate(true);
      setIsIntentModelShow(true);
      if (needToEmailName) setIsDesabled(false);
      if (paymentRes?.host_account_id) {
        const bytes = CryptoJS.AES.decrypt(
          paymentRes.host_account_id,
          CryptoJS.enc.Utf8.parse(encryKey),
          { mode: CryptoJS.mode.ECB, padding: CryptoJS.pad.Pkcs7 }
        );
        const newStripe = await loadStripe(
          process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY,
          { stripeAccount: bytes.toString(CryptoJS.enc.Utf8) }
        );
        setStripeInstance(newStripe);
      }
    } else {
      toast(responses?.message);
      setLoader(false);
      setIsIntentModelShow(false);
    }
  };

  return (
    <Modal
      show={show}
      dialogClassName="modal-90w modal-bottom"
      aria-labelledby="early-checkin-purchase-modal"
      className="earlycheckin"
    >
      <Modal.Header closeButton onClick={onClose} />
      <Modal.Body>
        <h2 className="mb-2" dangerouslySetInnerHTML={{ __html: cardTitle }} />
        <h6
          className="early_checkin_popup_heading"
          dangerouslySetInnerHTML={{ __html: cardBody }}
        />
        <hr className="mb-2" />

        <div className="range custom_select_drop_box mb-3">
          {!isIntentModelShow ? (
            timeInterval.length > 0 ? (
              <React.Fragment>
                <span className="dataLabel3" htmlFor="hours">
                  Do you want to arrive?
                </span>
                <Dropdown
                  options={timeInterval}
                  onChange={handleSelectChange}
                  value={selectedInterval}
                  disabled={isIntentModelShow}
                />
              </React.Fragment>
            ) : (
              ""
            )
          ) : (
            <React.Fragment>
              <span className="dataLabel3" htmlFor="hours">
                You want to check in early at:
              </span>
              <Form.Group className="mb-3">
                <Form.Control
                  type="text"
                  value={selectedIntervalLabel}
                  disabled={true}
                  className="input-text2-modal"
                />
              </Form.Group>
            </React.Fragment>
          )}
        </div>

        {needToEmailName && !isIntentModelShow && (
          <React.Fragment>
            <span className="dataLabel3" htmlFor="name">Name</span>
            <Form.Group className="mb-3">
              <Form.Control
                type="text"
                id="name"
                value={checkoutName}
                onChange={handleNameChange}
                className="input-text2-modal"
                required
              />
            </Form.Group>
            <span className="dataLabel3" htmlFor="email">Email</span>
            <Form.Group className="mb-3">
              <Form.Control
                type="email"
                id="email"
                value={checkoutEmail}
                onChange={handleEmailChange}
                className="input-text2-modal"
              />
            </Form.Group>
          </React.Fragment>
        )}

        <hr className="mb-3 mt-3" />

        {!isIntentCreate && (
          <div className="d-flex justify-content-between mb-4">
            {!isFreeCheckout ? (
              <>
                <Button className="outlinebutton cancel_btn" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  className="fillbutton"
                  onClick={createPaymentIntent}
                  disabled={isDesabled}
                >
                  {loader ? <SpinnerLoader /> : (isManualPresell ? "Request" : "Checkout")}
                </Button>
              </>
            ) : (
              <span dangerouslySetInnerHTML={{ __html: freeEarlyCheckinAlert }} />
            )}
          </div>
        )}

        {isIntentCreate && (
          <Elements
            stripe={stripeInstance}
            options={{ clientSecret }}
          >
            <EarlyCheckoutForm
              closeEarlyCheckinPaymentModel={onClose}
              selectedInterval={selectedInterval}
              bookingSlug={bookingSlug}
              paymentSlug={paymentSlug}
              earlyLatePurchased={onPurchased}
              submitButtonText={isManualPresell ? "Request" : "Submit"}
              isManualPresell={isManualPresell}
            />
          </Elements>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default EarlyCheckinPurchaseModal;
